import React from "react";
import {
  WSAlert,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Account,
  OAuthAuthorizationCandidate,
  OAuthAuthorizeContext,
  OAuthClientIdentityAssurance
} from "@wingspanhq/wingspan-api-sdk";
import { CandidateSelector } from "./CandidateSelector";
import { ClientInfo } from "./ClientInfo";
import { ScopesList } from "./ScopesList";
import { candidateAccountName } from "../hooks/useQueryCandidateAccounts";
import styles from "./AuthorizeConsent.module.scss";

interface AuthorizeConsentProps {
  context: OAuthAuthorizeContext;
  accountsById: Map<string, Account>;
  selectedIndex?: number;
  onSelectCandidate: (index: number) => void;
  onApprove: () => void;
  onDeny: () => void;
  isLoading: boolean;
}

export const AuthorizeConsent: React.FC<AuthorizeConsentProps> = ({
  context,
  accountsById,
  selectedIndex,
  onSelectCandidate,
  onApprove,
  onDeny,
  isLoading
}) => {
  const { client, candidates, consentRequired } = context;
  const selected: OAuthAuthorizationCandidate | undefined =
    selectedIndex === undefined ? undefined : candidates[selectedIndex];
  const isUnverifiedClient =
    client.identityAssurance === OAuthClientIdentityAssurance.UNVERIFIED_PUBLIC;

  return (
    <WSCard className={styles.card}>
      <ClientInfo client={client} />

      {isUnverifiedClient && (
        <WSAlert theme="warning" icon="alert" mb="L">
          <WSFlexBox direction="column" gap="XS">
            <WSText.Paragraph weight="medium">
              Wingspan hasn't verified this application
            </WSText.Paragraph>
            <WSText.ParagraphSm>
              Its name and logo are supplied by whoever registered it. Only
              continue if you recognise it and meant to connect it.
            </WSText.ParagraphSm>
          </WSFlexBox>
        </WSAlert>
      )}

      {candidates.length > 1 ? (
        <CandidateSelector
          candidates={candidates}
          accountsById={accountsById}
          selectedIndex={selectedIndex}
          onChange={onSelectCandidate}
        />
      ) : (
        selected && (
          <WSElement mb="L">
            <WSText.ParagraphSm color="gray600">Account</WSText.ParagraphSm>
            <WSText.Paragraph weight="medium">
              {candidateAccountName(selected.accountId, accountsById)}
            </WSText.Paragraph>
          </WSElement>
        )
      )}

      {selected && (
        <WSElement mb="L">
          <ScopesList resourceCategories={selected.resourceCategories} />
        </WSElement>
      )}

      <WSFlexBox gap="M">
        <WSButton.Secondary onClick={onDeny} disabled={isLoading} fullWidth>
          Deny
        </WSButton.Secondary>
        <WSButton.Primary
          onClick={onApprove}
          disabled={isLoading || !selected}
          loading={isLoading}
          fullWidth
        >
          {consentRequired ? "Approve" : "Continue"}
        </WSButton.Primary>
      </WSFlexBox>

      {(client.tosUri || client.policyUri) && (
        <WSElement mt="L" pt="L" className={styles.footerSection}>
          <WSFlexBox.Center gap="M">
            {client.tosUri && (
              <a
                href={client.tosUri}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.footerLink}
              >
                Terms of Service
              </a>
            )}
            {client.tosUri && client.policyUri && (
              <WSText.ParagraphSm color="gray400">•</WSText.ParagraphSm>
            )}
            {client.policyUri && (
              <a
                href={client.policyUri}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.footerLink}
              >
                Privacy Policy
              </a>
            )}
          </WSFlexBox.Center>
        </WSElement>
      )}
    </WSCard>
  );
};
